import { useState, useEffect, useCallback, useMemo, Fragment } from 'react'
import { biApi } from '../api/bi'
import { colors, font, text, space, radius, sx } from '../design/tokens'
import { fmtAbrev, fmtNum, PALETA_BI as PALETA, presetRangoBi as presetRango, PRESETS_BI as PRESETS } from '../utils/bi'
import Boton from '../components/Boton'
import MultiSelectFiltro from '../components/MultiSelectFiltro'

// Dimensiones con filtro multi-selección (campo del request ↔ faceta de dimensiones())
const MULTI_CAMPOS = [
  { campo: 'subrubro5', label: 'Categoría', dim: 'subrubros5' },
  { campo: 'rubro', label: 'Rubro', dim: 'rubros' },
  { campo: 'empresa', label: 'Empresa', dim: 'empresas' },
  { campo: 'origen', label: 'Origen', dim: 'origenes' },
]
const FILTROS_VACIOS = { subrubro5: [], rubro: [], empresa: [], origen: [], cuenta: '' }

function Delta({ pct, invertir }) {
  if (pct === null || pct === undefined) return <span style={{ fontSize: text.meta, color: colors.textMeta }}>—</span>
  const up = pct >= 0
  // En gastos, subir es "malo" (rojo) y bajar "bueno" (verde): invertimos el color.
  const positivo = invertir ? !up : up
  return (
    <span style={{ fontSize: text.sec, fontWeight: 600, color: positivo ? colors.ok : colors.err, ...sx.mono }}>
      {up ? '▲' : '▼'} {Math.abs(pct).toLocaleString('en-US', { maximumFractionDigits: 1 })}%
    </span>
  )
}

function KpiCard({ label, valor, delta, deltaInvertir, sub }) {
  return (
    <div style={{ flex: 1, minWidth: 150, background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: `${space.lg}px ${space.xl}px` }}>
      <div style={{ ...sx.cardTitle }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: colors.text, ...sx.mono, marginTop: space.xs }}>{valor}</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 2 }}>
        <span style={{ fontSize: text.meta, color: colors.textMeta }}>{sub}</span>
        {delta !== undefined && <Delta pct={delta} invertir={deltaInvertir} />}
      </div>
    </div>
  )
}

function LineaTendencia({ datos }) {
  const W = 920, H = 240, P = { t: 16, r: 16, b: 28, l: 56 }
  if (!datos.length) return <Vacio />
  const max = Math.max(...datos.map((d) => d.gasto), 1)
  const innerW = W - P.l - P.r, innerH = H - P.t - P.b
  const x = (i) => P.l + (datos.length === 1 ? innerW / 2 : (i / (datos.length - 1)) * innerW)
  const y = (v) => P.t + innerH - (v / max) * innerH
  const linea = datos.map((d, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(d.gasto).toFixed(1)}`).join(' ')
  const area = `${linea} L ${x(datos.length - 1).toFixed(1)} ${(P.t + innerH).toFixed(1)} L ${x(0).toFixed(1)} ${(P.t + innerH).toFixed(1)} Z`
  const ticks = [0, 0.5, 1].map((f) => ({ v: max * f, y: y(max * f) }))
  const cadaN = Math.ceil(datos.length / 12)

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto' }}>
      {ticks.map((t, i) => (
        <g key={i}>
          <line x1={P.l} x2={W - P.r} y1={t.y} y2={t.y} stroke={colors.borderSoft} />
          <text x={P.l - 8} y={t.y + 4} textAnchor="end" fontSize="10" fill={colors.textMeta} fontFamily={font.mono}>{fmtAbrev(t.v)}</text>
        </g>
      ))}
      <path d={area} fill={colors.t80Text} opacity="0.08" />
      <path d={linea} fill="none" stroke={colors.t80Text} strokeWidth="2" strokeLinejoin="round" />
      {datos.map((d, i) => (i % cadaN === 0 || i === datos.length - 1) && (
        <text key={i} x={x(i)} y={H - 8} textAnchor="middle" fontSize="10" fill={colors.textMeta} fontFamily={font.mono}>{d.periodo}</text>
      ))}
    </svg>
  )
}

function Dona({ datos, campo }) {
  const total = datos.reduce((a, d) => a + d.gasto, 0)
  if (!total) return <Vacio />
  const top = datos.slice(0, 8)
  const otros = datos.slice(8).reduce((a, d) => a + d.gasto, 0)
  const items = otros > 0 ? [...top, { [campo]: 'Otros', gasto: otros }] : top
  const R = 70, r = 44, cx = 90, cy = 90
  let ang = -Math.PI / 2
  const arcos = items.map((d, i) => {
    const frac = d.gasto / total
    const a0 = ang, a1 = ang + frac * 2 * Math.PI
    ang = a1
    const large = a1 - a0 > Math.PI ? 1 : 0
    const p = (rad, a) => [cx + rad * Math.cos(a), cy + rad * Math.sin(a)]
    const [x0, y0] = p(R, a0), [x1, y1] = p(R, a1), [x2, y2] = p(r, a1), [x3, y3] = p(r, a0)
    return { d: `M ${x0} ${y0} A ${R} ${R} 0 ${large} 1 ${x1} ${y1} L ${x2} ${y2} A ${r} ${r} 0 ${large} 0 ${x3} ${y3} Z`, color: PALETA[i % PALETA.length], item: d, frac }
  })
  return (
    <div style={{ display: 'flex', gap: space.xl, alignItems: 'center', flexWrap: 'wrap' }}>
      <svg viewBox="0 0 180 180" style={{ width: 180, height: 180, flexShrink: 0 }}>
        {arcos.map((a, i) => <path key={i} d={a.d} fill={a.color} />)}
      </svg>
      <div style={{ flex: 1, minWidth: 200 }}>
        {arcos.map((a, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: space.md, padding: '3px 0', fontSize: text.sec }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: a.color, flexShrink: 0 }} />
            <span style={{ flex: 1, color: colors.text, fontFamily: font.sans, textTransform: 'capitalize', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{(a.item[campo] || '').toLowerCase()}</span>
            <span style={{ ...sx.mono, color: colors.textSec }}>{(a.frac * 100).toFixed(1)}%</span>
            <span style={{ ...sx.mono, color: colors.text, width: 70, textAlign: 'right' }}>{fmtAbrev(a.item.gasto)}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function Ranking({ datos, getLabel, getValor, getSub, color = colors.t80Text }) {
  if (!datos.length) return <Vacio />
  const max = Math.max(...datos.map(getValor), 1)
  return (
    <div>
      {datos.map((d, i) => {
        const v = getValor(d)
        return (
          <div key={i} style={{ padding: '6px 0', borderBottom: `1px solid ${colors.borderSoft}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: space.md, marginBottom: 3 }}>
              <span style={{ fontSize: text.body, color: colors.text, fontFamily: font.sans, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                <span style={{ color: colors.textMeta, ...sx.mono, marginRight: 6 }}>{i + 1}</span>{getLabel(d)}
              </span>
              <span style={{ ...sx.mono, fontSize: text.body, fontWeight: 600, color: colors.text, flexShrink: 0 }}>{fmtAbrev(v)}</span>
            </div>
            <div style={{ height: 6, background: colors.borderSoft, borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ width: `${(v / max) * 100}%`, height: '100%', background: color, borderRadius: 3 }} />
            </div>
            {getSub && <div style={{ fontSize: text.meta, color: colors.textMeta, marginTop: 2 }}>{getSub(d)}</div>}
          </div>
        )
      })}
    </div>
  )
}

// ─── Proyección de cierre del mes (curva intra-mes del gasto) ──────────
function ProyeccionMes({ p }) {
  if (!p || !p.proyeccion) {
    return <div style={{ fontSize: text.sec, color: colors.textMeta }}>Sin datos suficientes para proyectar.</div>
  }
  const dato = (label, valor, extra) => (
    <div style={{ flex: 1, minWidth: 130 }}>
      <div style={{ ...sx.cardTitle }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: colors.text, ...sx.mono, marginTop: 2 }}>{valor}</div>
      {extra && <div style={{ fontSize: text.meta, color: colors.textMeta, marginTop: 2 }}>{extra}</div>}
    </div>
  )
  return (
    <div style={{ display: 'flex', gap: space.xl, flexWrap: 'wrap', alignItems: 'flex-end' }}>
      {dato('Gastado en el mes', fmtAbrev(p.mtd), `al día ${p.dia} · ${p.pct}% del mes`)}
      {dato('Proyección de cierre', fmtAbrev(p.proyeccion),
        p.vs_anio_pasado !== null ? <>vs mismo mes año pasado <Delta pct={p.vs_anio_pasado} invertir /></> : 'ARS nominal')}
      {dato('Mismo mes año pasado', fmtAbrev(p.mes_anio_pasado), 'real cerrado')}
    </div>
  )
}

function Vacio() {
  return <div style={{ padding: space['3xl'], textAlign: 'center', color: colors.textMeta, fontSize: text.sec }}>Sin datos en el período</div>
}

function Card({ titulo, children, ancho, nota }) {
  return (
    <div style={{ flex: ancho || 1, minWidth: 320, background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: space.xl }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: space.lg }}>
        <h3 style={{ ...sx.cardTitle, margin: 0 }}>{titulo}</h3>
        {nota && <span style={{ fontSize: text.meta, color: colors.textMeta }}>{nota}</span>}
      </div>
      {children}
    </div>
  )
}

// Detalle de cuentas mes por mes (últimos 12), agrupadas por categoría → nombre.
const MESES3 = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']
function mesLabel(p) {
  const [y, m] = String(p).split('-')
  return `${MESES3[parseInt(m, 10) - 1]} '${y.slice(2)}`
}
function DetalleCuentas12M({ detalle }) {
  const [abiertas, setAbiertas] = useState({})
  if (!detalle || !(detalle.categorias || []).length) {
    return <div style={{ color: colors.textMeta, fontSize: text.sec }}>Sin movimientos en los últimos 12 meses.</div>
  }
  const per = detalle.periodos
  const todas = detalle.categorias.map((c) => c.categoria)
  const allOpen = todas.every((c) => abiertas[c])
  const toggle = (c) => setAbiertas((s) => ({ ...s, [c]: !s[c] }))
  const toggleAll = () => { const open = !allOpen; setAbiertas(Object.fromEntries(todas.map((c) => [c, open]))) }
  const num = (v, w = 400, col) => (
    <td style={{ ...sx.mono, padding: '4px 5px', textAlign: 'right', fontSize: text.meta, fontWeight: w, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', color: col || (v < 0 ? '#0a7d3c' : v ? colors.text : colors.textMeta) }}>
      {v ? fmtAbrev(v) : '·'}
    </td>
  )
  const th = (txt, extra) => (
    <th style={{ padding: '6px 5px', textAlign: 'right', fontSize: text.meta, textTransform: 'uppercase', letterSpacing: '.02em', color: colors.textMeta, fontWeight: 600, whiteSpace: 'nowrap', ...extra }}>{txt}</th>
  )
  return (
    <div>
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: text.sec, color: colors.textSec, cursor: 'pointer', marginBottom: space.md }}>
        <input type="checkbox" checked={allOpen} onChange={toggleAll} /> {allOpen ? 'Cerrar todo' : 'Expandir todo'}
      </label>
      <table style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed' }}>
        <thead>
          <tr style={{ borderBottom: `2px solid ${colors.border}` }}>
            {th('Cuenta', { textAlign: 'left', width: '17%' })}
            {per.map((p) => <th key={p} style={{ padding: '6px 5px', textAlign: 'right', fontSize: text.meta, color: colors.textMeta, fontWeight: 600, whiteSpace: 'nowrap' }}>{mesLabel(p)}</th>)}
            {th('Total', { color: colors.text })}
          </tr>
        </thead>
        <tbody>
          {detalle.categorias.map((cat) => {
            const open = !!abiertas[cat.categoria]
            return (
              <Fragment key={cat.categoria}>
                <tr onClick={() => toggle(cat.categoria)} style={{ cursor: 'pointer', background: colors.bgSoft || '#f6f7f9', borderBottom: `1px solid ${colors.border}` }}>
                  <td style={{ padding: '6px 5px', fontWeight: 700, fontSize: text.sec, color: colors.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <span style={{ display: 'inline-block', width: 13, color: colors.textMeta }}>{open ? '▾' : '▸'}</span>{cat.categoria}
                  </td>
                  {per.map((p) => num(cat.meses[p], 700))}
                  {num(cat.total, 700, colors.text)}
                </tr>
                {open && cat.cuentas.map((cta) => (
                  <tr key={cta.cuenta} style={{ borderBottom: `1px solid ${colors.borderSoft}` }}>
                    <td style={{ padding: '4px 5px 4px 20px', fontSize: text.meta, color: colors.textSec, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={cta.cuenta}>{cta.cuenta}</td>
                    {per.map((p) => num(cta.meses[p]))}
                    {num(cta.total, 600, colors.text)}
                  </tr>
                ))}
              </Fragment>
            )
          })}
          <tr style={{ borderTop: `2px solid ${colors.border}` }}>
            <td style={{ padding: '7px 5px', fontWeight: 700, fontSize: text.sec, color: colors.text }}>TOTAL</td>
            {per.map((p) => num(detalle.totales[p], 700, colors.text))}
            {num(detalle.total, 700, colors.text)}
          </tr>
        </tbody>
      </table>
    </div>
  )
}

// Variación % (para gastos: subir es "más gasto" = rojo, bajar = verde).
function VarPct({ v }) {
  if (v == null) return <span style={{ color: colors.textMeta, fontSize: text.body }}>—</span>
  const up = v > 0
  return <span style={{ fontSize: text.body, fontWeight: 600, color: up ? colors.err : '#0a7d3c', whiteSpace: 'nowrap' }}>{up ? '▲' : '▼'} {Math.abs(v).toLocaleString('en-US', { maximumFractionDigits: 1 })}%</span>
}

// dd/mm/aa de un ISO YYYY-MM-DD
function fdate(s) { const [y, m, d] = String(s).split('-'); return `${d}/${m}/${y.slice(2)}` }

// Resumen tipo contable: categoría → cuenta, con gasto del rango + valor de referencia
// (período anterior) + var %. Con abrir/cerrar todo.
function ResumenContable({ resumen, rango }) {
  const [abiertas, setAbiertas] = useState({})
  if (!resumen || !(resumen.categorias || []).length) {
    return <div style={{ color: colors.textMeta, fontSize: text.body }}>Sin gastos en el período.</div>
  }
  const todas = resumen.categorias.map((c) => c.categoria)
  const allOpen = todas.every((c) => abiertas[c])
  const toggle = (c) => setAbiertas((s) => ({ ...s, [c]: !s[c] }))
  const toggleAll = () => { const open = !allOpen; setAbiertas(Object.fromEntries(todas.map((c) => [c, open]))) }
  const th = { padding: '7px 12px', fontSize: text.sec, color: colors.textMeta, fontWeight: 600, whiteSpace: 'nowrap' }
  const sub = { fontWeight: 400, fontSize: text.meta, color: colors.textMeta }
  const money = (v, w = 400, col, size = text.sec) => (
    <td style={{ ...sx.mono, padding: '6px 12px', textAlign: 'right', fontSize: size, fontWeight: w, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', color: col || (v < 0 ? '#0a7d3c' : v ? colors.text : colors.textMeta) }}>{v ? fmtAbrev(v) : '·'}</td>
  )
  const rp = resumen.rango_previo
  return (
    <div>
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: text.sec, color: colors.textSec, cursor: 'pointer', marginBottom: space.md }}>
        <input type="checkbox" checked={allOpen} onChange={toggleAll} /> {allOpen ? 'Cerrar todo' : 'Abrir todo'}
      </label>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 560 }}>
          <thead>
            <tr style={{ borderBottom: `2px solid ${colors.border}` }}>
              <th style={{ ...th, textAlign: 'left' }}>Cuenta</th>
              <th style={{ ...th, textAlign: 'right' }}>Gasto{rango && <div style={sub}>{fdate(rango.desde)}–{fdate(rango.hasta)}</div>}</th>
              <th style={{ ...th, textAlign: 'right' }}>Referencia{rp && <div style={sub}>{fdate(rp.desde)}–{fdate(rp.hasta)}</div>}</th>
              <th style={{ ...th, textAlign: 'right' }}>Var %</th>
            </tr>
          </thead>
          <tbody>
            {resumen.categorias.map((cat) => {
              const open = !!abiertas[cat.categoria]
              return (
                <Fragment key={cat.categoria}>
                  <tr onClick={() => toggle(cat.categoria)} style={{ cursor: 'pointer', background: colors.bgSoft || '#f6f7f9', borderBottom: `1px solid ${colors.border}` }}>
                    <td style={{ padding: '7px 12px', fontWeight: 700, fontSize: text.body, color: colors.text, whiteSpace: 'nowrap' }}>
                      <span style={{ display: 'inline-block', width: 15, color: colors.textMeta }}>{open ? '▾' : '▸'}</span>{cat.categoria}
                    </td>
                    {money(cat.gasto, 700, colors.text, text.body)}
                    {money(cat.previo, 400, colors.textSec, text.sec)}
                    <td style={{ padding: '7px 12px', textAlign: 'right' }}><VarPct v={cat.var} /></td>
                  </tr>
                  {open && cat.cuentas.map((c) => (
                    <tr key={c.cuenta} style={{ borderBottom: `1px solid ${colors.borderSoft}` }}>
                      <td style={{ padding: '5px 12px 5px 30px', fontSize: text.sec, color: colors.textSec, whiteSpace: 'nowrap' }}>{c.cuenta}</td>
                      {money(c.gasto)}
                      {money(c.previo, 400, colors.textMeta)}
                      <td style={{ padding: '5px 12px', textAlign: 'right' }}><VarPct v={c.var} /></td>
                    </tr>
                  ))}
                </Fragment>
              )
            })}
            <tr style={{ borderTop: `2px solid ${colors.border}` }}>
              <td style={{ padding: '8px 12px', fontWeight: 700, fontSize: text.body, color: colors.text }}>TOTAL</td>
              {money(resumen.total, 700, colors.text, text.body)}
              {money(resumen.total_previo, 400, colors.textSec, text.sec)}
              <td style={{ padding: '8px 12px', textAlign: 'right' }}><VarPct v={resumen.var} /></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default function BiGastosPage() {
  const [preset, setPreset] = useState('anio')
  const [[desde, hasta], setRango] = useState(presetRango('anio'))
  const [fil, setFil] = useState(FILTROS_VACIOS)
  const [ctaBuf, setCtaBuf] = useState('')
  const [dims, setDims] = useState({ subrubros5: [], empresas: [], origenes: [], rubros: [], rango_datos: {} })
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [moneda, setMoneda] = useState('ars')
  const [excluirAjustes, setExcluirAjustes] = useState(false)

  useEffect(() => { biApi.gastosDimensiones().then(({ data }) => setDims(data)).catch(() => {}) }, [])

  useEffect(() => {
    const t = setTimeout(() => setFil((f) => ({ ...f, cuenta: ctaBuf })), 400)
    return () => clearTimeout(t)
  }, [ctaBuf])

  const cargar = useCallback(() => {
    setLoading(true)
    biApi.gastos({ desde, hasta, moneda, excluir_ajustes: excluirAjustes ? 1 : 0, ...fil })
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false))
  }, [desde, hasta, moneda, excluirAjustes, fil])
  useEffect(() => { cargar() }, [cargar])

  const aplicarPreset = (k) => { setPreset(k); setRango(presetRango(k)) }
  const setMulti = (campo) => (vals) => setFil((f) => ({ ...f, [campo]: vals }))
  const limpiarFiltros = () => { setFil(FILTROS_VACIOS); setCtaBuf('') }
  const hayFiltros = MULTI_CAMPOS.some((c) => fil[c.campo].length) || fil.cuenta
  const k = data?.kpis

  const st = useMemo(() => ({
    input: { padding: '7px 10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: text.sec, fontFamily: font.sans, color: colors.text, background: colors.bg },
    presetBtn: (on) => ({ ...sx.pill, ...(on ? sx.pillActive : {}) }),
  }), [])

  return (
    <div style={{ padding: space['2xl'], maxWidth: 1240, margin: '0 auto', fontFamily: font.sans }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: space.md, marginBottom: space.lg }}>
        <div>
          <div style={{ fontSize: text.meta, fontWeight: 600, color: colors.textMeta, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Inteligencia de costos</div>
          <h1 style={{ margin: '2px 0 0', fontSize: text.title + 6, color: colors.text }}>Gastos</h1>
        </div>
        {dims.rango_datos?.desde && (
          <div style={{ fontSize: text.meta, color: colors.textMeta, ...sx.mono }}>
            datos: {dims.rango_datos.desde} → {dims.rango_datos.hasta}
          </div>
        )}
      </div>

      {/* Filtros: fecha + moneda */}
      <div style={{ display: 'flex', gap: space.md, alignItems: 'center', flexWrap: 'wrap', marginBottom: space.md }}>
        {PRESETS.map((p) => <button key={p.k} style={st.presetBtn(preset === p.k)} onClick={() => aplicarPreset(p.k)}>{p.label}</button>)}
        <span style={{ width: 1, height: 22, background: colors.border }} />
        <input type="date" value={desde} onChange={(e) => { setPreset(''); setRango([e.target.value, hasta]) }} style={st.input} />
        <span style={{ color: colors.textMeta }}>→</span>
        <input type="date" value={hasta} onChange={(e) => { setPreset(''); setRango([desde, e.target.value]) }} style={st.input} />
        <span style={{ width: 1, height: 22, background: colors.border }} />
        <div style={{ display: 'inline-flex', border: `1px solid ${colors.border}`, borderRadius: radius.sm, overflow: 'hidden' }}>
          {[['ars', '$ ARS'], ['usd', 'US$'], ['real', '$ hoy']].map(([m, lbl]) => (
            <button key={m} onClick={() => setMoneda(m)}
              style={{ padding: '7px 12px', border: 'none', cursor: 'pointer', fontSize: text.sec, fontWeight: 600, fontFamily: font.sans, background: moneda === m ? colors.link : colors.bg, color: moneda === m ? '#fff' : colors.textSec }}>
              {lbl}
            </button>
          ))}
        </div>
        {moneda === 'usd' && <span style={{ fontSize: text.meta, color: colors.textMeta }}>convertido al blue de cada día</span>}
        {moneda === 'real' && <span style={{ fontSize: text.meta, color: colors.textMeta }}>pesos constantes de hoy (deflactado por IPC)</span>}
      </div>

      {/* Filtros: dimensiones + búsqueda de cuenta + toggle ajustes */}
      <div style={{ display: 'flex', gap: space.sm, alignItems: 'center', flexWrap: 'wrap', marginBottom: space.xl }}>
        {MULTI_CAMPOS.map((m) => (
          <MultiSelectFiltro
            key={m.campo}
            label={m.label}
            options={(dims[m.dim] || []).map((v) => ({ value: v, label: String(v) }))}
            selected={fil[m.campo]}
            onChange={setMulti(m.campo)}
          />
        ))}
        <input value={ctaBuf} onChange={(e) => setCtaBuf(e.target.value)} placeholder="Cuenta…" style={{ ...st.input, width: 150 }} />
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: text.sec, color: colors.textSec, cursor: 'pointer' }}>
          <input type="checkbox" checked={excluirAjustes} onChange={(e) => setExcluirAjustes(e.target.checked)} />
          Excluir ajuste por inflación
        </label>
        {hayFiltros && <Boton variante="ghost" sm onClick={limpiarFiltros}>Limpiar todo</Boton>}
      </div>

      {loading && !data ? (
        <div style={{ padding: space['3xl'], textAlign: 'center', color: colors.textMeta }}>Cargando…</div>
      ) : !data ? (
        <div style={{ padding: space['3xl'], textAlign: 'center', color: colors.err }}>No se pudieron cargar los datos.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: space.lg, opacity: loading ? 0.6 : 1, transition: 'opacity .15s' }}>
          {/* KPIs */}
          <div style={{ display: 'flex', gap: space.lg, flexWrap: 'wrap' }}>
            <KpiCard label="Gasto total" valor={fmtAbrev(k.gasto)} delta={k.var.gasto} deltaInvertir sub={`saldo · ${{ ars: 'ARS', usd: 'USD', real: '$ hoy' }[moneda]}${excluirAjustes ? ' · sin ajuste' : ''}`} />
            <KpiCard label="Promedio mensual" valor={fmtAbrev(k.promedio_mensual)} sub="del período" />
            <KpiCard label="Asientos" valor={fmtNum(k.asientos)} delta={k.var.asientos} deltaInvertir sub={`${fmtNum(k.categorias)} categorías`} />
            {data.mes_anterior && (
              <KpiCard label={`Mes anterior · ${data.mes_anterior.etiqueta}`} valor={fmtAbrev(data.mes_anterior.gasto)}
                delta={data.mes_anterior.var} deltaInvertir sub="cerrado · período contable" />
            )}
            <KpiCard label="Ajuste inflación" valor={fmtAbrev(k.ajuste_inflacion)} sub={`RECPAM · ${excluirAjustes ? 'excluido' : 'incluido'}`} />
          </div>

          {/* Proyección de cierre del mes */}
          <Card titulo="Proyección de cierre — mes en curso" nota="curva intra-mes · ARS">
            <ProyeccionMes p={data.proyeccion} />
          </Card>

          {/* Tendencia */}
          <Card titulo="Evolución del gasto">
            <LineaTendencia datos={data.tendencia} />
          </Card>

          {/* Divisor: categoría (subrubro5) */}
          <div style={{ display: 'flex', gap: space.lg, flexWrap: 'wrap' }}>
            <Card titulo="Gasto por categoría" nota="SUBRUBRO 5"><Dona datos={data.por_subrubro5} campo="subrubro5" /></Card>
          </div>

          {/* Resumen contable: categoría › cuenta + var % vs período anterior */}
          <Card titulo="Resumen contable — gastos por categoría y cuenta" nota="var % vs período anterior de igual duración">
            <ResumenContable resumen={data.resumen_contable} rango={data.rango} />
          </Card>

          {/* Detalle de cuentas mes por mes — últimos 12 meses */}
          <Card titulo="Detalle de cuentas — últimos 12 meses" nota="categoría › cuenta · ARS · período contable">
            <DetalleCuentas12M detalle={data.detalle_cuentas} />
          </Card>
        </div>
      )}
    </div>
  )
}
